import { useCallback, useEffect, useMemo, useState } from 'react'

import { API_BASE } from '#api/client.js'
import type { ToastType } from '#components/Toast'

interface UseBulkUpdateOptions {
  /** Ids currently visible in the list; selections outside it are dropped */
  visibleIds: string[]
  getEndpoint: (id: string) => string
  noun: string
  successMessage?: (count: number) => string
  onUpdatesTriggered?: () => void
}

export interface BulkUpdateStatus {
  type: ToastType
  message: string
}

export interface BulkUpdateState {
  selectedIds: Set<string>
  selectedCount: number
  isAllVisibleSelected: boolean
  isTriggering: boolean
  status: BulkUpdateStatus | null
  dismissStatus: () => void
  toggleSelection: (id: string) => void
  toggleSelectVisible: () => void
  triggerUpdates: () => Promise<void>
}

const postUpdate = async (url: string): Promise<void> => {
  const response = await fetch(url, { method: 'POST', credentials: 'include' })
  if (response.ok) return

  let errorMessage = 'Failed to trigger update'
  try {
    const body = await response.json()
    errorMessage = body?.error || errorMessage
  } catch {
    // no-op
  }
  throw new Error(errorMessage)
}

export const useBulkUpdate = ({
  visibleIds,
  getEndpoint,
  noun,
  successMessage,
  onUpdatesTriggered,
}: UseBulkUpdateOptions): BulkUpdateState => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isTriggering, setIsTriggering] = useState(false)
  const [status, setStatus] = useState<BulkUpdateStatus | null>(null)

  const dismissStatus = useCallback(() => setStatus(null), [])

  useEffect(() => {
    setSelectedIds(prev => {
      const validIds = new Set(visibleIds)
      const next = new Set([...prev].filter(id => validIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleIds])

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const toggleSelectVisible = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      const shouldSelectAll = visibleIds.some(id => !next.has(id))
      for (const id of visibleIds) {
        if (shouldSelectAll) {
          next.add(id)
        } else {
          next.delete(id)
        }
      }
      return next
    })
  }, [visibleIds])

  const isAllVisibleSelected = useMemo(
    () => visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id)),
    [selectedIds, visibleIds],
  )

  const triggerUpdates = useCallback(async () => {
    if (selectedIds.size === 0) return

    setIsTriggering(true)
    setStatus(null)

    const ids = Array.from(selectedIds)

    try {
      const results = await Promise.allSettled(
        ids.map(async id => {
          await postUpdate(`${API_BASE}/${getEndpoint(id)}`)
          return id
        }),
      )

      const successIds = results
        .filter(
          (result): result is PromiseFulfilledResult<string> =>
            result.status === 'fulfilled',
        )
        .map(result => result.value)
      const failureMessages = results
        .filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected',
        )
        .map(result =>
          result.reason instanceof Error
            ? result.reason.message
            : String(result.reason ?? 'Unknown error'),
        )

      if (successIds.length > 0) {
        setSelectedIds(prev => {
          const next = new Set(prev)
          successIds.forEach(id => next.delete(id))
          return next
        })
        onUpdatesTriggered?.()
      }

      if (failureMessages.length === 0) {
        setStatus({
          type: 'success',
          message: successMessage
            ? successMessage(successIds.length)
            : `Triggered updates for ${successIds.length} ${noun}${successIds.length === 1 ? '' : 's'}.`,
        })
      } else if (successIds.length > 0) {
        setStatus({
          type: 'warning',
          message: `Triggered ${successIds.length} updates, ${failureMessages.length} failed: ${failureMessages.join('; ')}`,
        })
      } else {
        setStatus({
          type: 'error',
          message: `Failed to trigger updates: ${failureMessages.join('; ')}`,
        })
      }
    } finally {
      setIsTriggering(false)
    }
  }, [selectedIds, getEndpoint, noun, successMessage, onUpdatesTriggered])

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    isAllVisibleSelected,
    isTriggering,
    status,
    dismissStatus,
    toggleSelection,
    toggleSelectVisible,
    triggerUpdates,
  }
}
